/**
 * Normalize time input to HH:MM for storage.
 * Accepts 9:30, 09:30, and Chinese full-width colon (9：30).
 * Returns null if invalid.
 */
export function normalizeTime(input) {
  if (input == null) return null

  const trimmed = String(input).trim()
  if (!trimmed) return null

  const normalized = trimmed.replace(/：/g, ':')
  const match = normalized.match(/^(\d{1,2}):(\d{1,2})$/)

  if (!match) return null

  const hour = parseInt(match[1], 10)
  const minute = parseInt(match[2], 10)

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * Normalize single HH:MM or range HH:MM-HH:MM (Event start–end).
 */
export function normalizeTimeOrRange(input) {
  if (input == null) return null
  const trimmed = String(input).trim().replace(/：/g, ':')
  if (!trimmed) return null

  if (trimmed.includes('-')) {
    const parts = trimmed.split('-').map(s => s.trim()).filter(Boolean)
    if (parts.length !== 2) return null
    const start = normalizeTime(parts[0])
    const end = normalizeTime(parts[1])
    if (!start || !end) return null
    return `${start}-${end}`
  }

  return normalizeTime(trimmed)
}

/** Parse stored time into { start, end } for Event UI. */
export function parseTimeRange(time) {
  if (!time) return { start: '', end: '' }
  const trimmed = String(time).trim().replace(/：/g, ':')
  if (trimmed.includes('-')) {
    const [a, b] = trimmed.split('-').map(s => s.trim())
    return {
      start: normalizeTime(a) || a || '',
      end: normalizeTime(b) || b || ''
    }
  }
  return { start: normalizeTime(trimmed) || trimmed, end: '' }
}

/** Combine Event UI start/end into one storage string. */
export function combineTimeRange(start, end) {
  const s = normalizeTime(start)
  const e = normalizeTime(end)
  if (s && e) return `${s}-${e}`
  if (s) return s
  return null
}

/**
 * Display time without leading zero on hour (09:30 → 9:30).
 */
export function formatTimeForDisplay(time) {
  if (time == null || time === '') return null

  const trimmed = String(time).trim().replace(/：/g, ':')
  if (trimmed.includes('-')) {
    const [a, b] = trimmed.split('-').map(s => s.trim())
    const left = formatTimeForDisplay(a)
    const right = formatTimeForDisplay(b)
    if (left && right) return `${left}-${right}`
    return trimmed
  }

  const match = trimmed.match(/^(\d{1,2}):(\d{1,2})$/)

  if (!match) return trimmed

  const hour = parseInt(match[1], 10)
  const minute = parseInt(match[2], 10)

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return trimmed

  return `${hour}:${String(minute).padStart(2, '0')}`
}
